import React, { useCallback, useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { useDropzone } from "react-dropzone";
import { uploadAndProcessFile } from "../utils/fileUpload";
import "../assets/styles/FileUploadModal.css";
import "../assets/styles/ChatMessages.css";
import { useAuth } from "./auth/AuthProvider";
import { useBedrockClient } from '../context/BedrockClientContext';
import { useDynamoUtils } from '../context/DynamoUtilsContext';
import { useSession } from '../context/SessionContext';

// Helper function to detect OS
const detectOS = () => {
  const userAgent = window.navigator.userAgent;
  if (userAgent.indexOf("Mac") !== -1) return "macOS";
  if (userAgent.indexOf("Windows") !== -1) return "Windows";
  if (userAgent.indexOf("Linux") !== -1) return "Linux";
  return "Unknown";
};

// Helper to detect mobile/tablet devices
const isMobileOrTablet = () => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
};

const ACCEPT = {
  // List of accepted image types for file uploads. Each extension must start
  // with a dot to satisfy react-dropzone's validation rules.
  "image/*": [".png", ".jpg", ".jpeg", ".webp", ".heic"],
  // Temporarily disabling PDF uploads as per requirement
  // "application/pdf": [".pdf"],
};
const MAX_BYTES = 5 * 1024 * 1024; // 5 MB per file

export default function FileUploadModal({ onClose, onSuccess, initialMode = "upload", sessionId }) {
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState([]);
  const [mode, setMode] = useState(initialMode); // "upload" or "camera" (for mobile only)
  const { user } = useAuth(); // Get user from auth context
  const bedrockClientRef = useBedrockClient();
  const dynamoUtilsRef = useDynamoUtils();
  const { sessionId: globalSessionId } = useSession();
  const osType = detectOS();
  const isMobile = isMobileOrTablet();

  // Initialize camera mode when component mounts on mobile/tablet if initialMode is 'screenshot'
  useEffect(() => {
    if (initialMode === "screenshot" && !busy && isMobile) {
      captureFromCamera();
    }
  }, [initialMode, busy]);

  const uploadFile = async (file) => {
    setBusy(true);
    setErrors([]);

    try {
      // Get user ID from auth context
      const userId = user?.sub;
      if (!userId) {
        throw new Error("You must be logged in to upload files");
      }

      const effectiveSessionId = sessionId || globalSessionId;

      const result = await uploadAndProcessFile(file, {
        userId,
        sessionId: effectiveSessionId,
        bedrockClient: bedrockClientRef.current,
        dynamoUtils: dynamoUtilsRef.current,
      });

      if (result && onSuccess) {
        onSuccess(result);
      }
    } catch (error) {
      console.error("File upload error:", error);
      setErrors([error.message || "Failed to upload file"]);
    } finally {
      setBusy(false);
    }
  };

  // Process a file (whether uploaded or from camera)
  const processFile = async (file) => {
    try {
      await uploadFile(file);
    } catch (e) {
      setErrors([`${file.name}: upload error: ${e.message}`]);
      console.error("File upload error:", e);
      return false;
    }
  };

  // Validate + upload handler
  const handleFiles = useCallback(
    async (files) => {
      const problems = [];
      setBusy(true);
      setErrors([]);

      for (const f of files) {
        if (f.size > MAX_BYTES) {
          problems.push(`${f.name}: larger than 5 MB`);
          continue;
        }

        // If we get here, the file is valid
        const success = await processFile(f);
        if (success) return; // onSuccess was called
      }

      // If we get here, we have problems
      setErrors(problems);
      setBusy(false);
    },
    [processFile]
  );

  // Configure dropzone
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: ACCEPT,
    maxSize: MAX_BYTES,
    multiple: false,
    disabled: busy,
    onDrop: handleFiles,
  });

  // Function to capture from camera (mobile)
  const captureFromCamera = () => {
    // Only proceed on mobile/tablet devices
    if (!isMobile) return;

    // Create a file input element programmatically
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.capture = "camera"; // This will open the camera on mobile devices

    input.onchange = (e) => {
      const file = e.target.files[0];
      if (file) {
        // Process the captured image
        setBusy(true);
        processFile(file).then(() => {
          setBusy(false);
        });
      }
    };

    // Trigger the file input click
    input.click();
  };

  // Create portal for modal
  return createPortal(
    <div className="file-upload-modal-backdrop">
      <div className="file-upload-modal">
        <div className="file-upload-modal-header">
          <h3>Upload Image</h3>
          <button
            className="file-upload-modal-close-btn"
            onClick={onClose}
            disabled={busy}
          >
            ×
          </button>
        </div>

        <div
          className={`file-upload-dropzone ${isDragActive ? "active" : ""} ${
            busy ? "busy" : ""
          }`}
          {...getRootProps()}
        >
          <input {...getInputProps()} />

          {busy ? (
            <div className="upload-busy-indicator">
              <div className="spinner"></div>
              <p>Processing file...</p>
            </div>
          ) : (
            <>
              <div className="upload-icon">📁</div>
              <p>
                {isDragActive
                  ? "Drop your image here..."
                  : "Drag and drop an image, or click to select"}
              </p>
              <p className="upload-hint">
                Accepted formats: PNG, JPG, JPEG, WebP, HEIC
              </p>
              <p className="upload-hint">Maximum size: 5 MB</p>
            </>
          )}
        </div>

        {/* Screenshot instructions section - always visible */}
        <div className="screenshot-help">
          <h4>How to capture your screen:</h4>
          <p>
            Due to browser security restrictions, we recommend using your
            operating system's screenshot tools:
          </p>

          {osType === "macOS" && (
            <ul>
              <li>
                <strong>For area selection:</strong> Press{" "}
                <code>Command (⌘) + Shift + 4</code>
              </li>
              <li>
                <strong>For entire screen:</strong> Press{" "}
                <code>Command (⌘) + Shift + 3</code>
              </li>
              <li>The screenshot will be saved to your desktop</li>
              <li>Drag and drop the saved image here</li>
            </ul>
          )}

          {osType === "Windows" && (
            <ul>
              <li>
                <strong>For area selection:</strong> Press{" "}
                <code>Windows + Shift + S</code>
              </li>
              <li>The screenshot will be copied to your clipboard</li>
              <li>Paste it into an image editor (like Paint), save it</li>
              <li>Drag and drop the saved image here</li>
            </ul>
          )}

          {osType === "Linux" && (
            <ul>
              <li>
                <strong>For area selection:</strong> Press{" "}
                <code>Print Screen</code> or use your distribution's screenshot
                tool
              </li>
              <li>Save the image to your computer</li>
              <li>Drag and drop the saved image here</li>
            </ul>
          )}

          {osType === "Unknown" && (
            <ul>
              <li>Use your operating system's screenshot tools</li>
              <li>Save the image to your computer</li>
              <li>Drag and drop the saved image here</li>
            </ul>
          )}
        </div>

        {/* Camera button - only shown on mobile/tablet */}
        {isMobile && (
          <div className="camera-option">
            <button
              className="camera-button"
              onClick={captureFromCamera}
              disabled={busy}
            >
              Take a photo with camera
            </button>
          </div>
        )}

        {errors.length > 0 && (
          <div className="file-upload-errors">
            <h4>Upload Failed</h4>
            <ul>
              {errors.map((err, i) => (
                <li key={i}>{err}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}